import React from "react";
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { LuAlertCircle, LuLayers, LuRuler } from "react-icons/lu";

import { useAppSelector } from "../../app/hooks";
import { getSizeUnit } from "../../features/appSettings/appSettingsSlice";
import { sizeLabel } from "../../static/itemSize";
import { PartyTypeProps } from "../../static/partyType";
import { RateField, rateField, rateLabel } from "../../static/processRate";
import { ItemsProps } from "../../types/item";
import { PartyRateRowInput, PartyRatesFormInput } from "../../types/parties";
import RateInput from "../ui/RateInput";
import cx from "../ui/cx";
import {
  commonRateRowClass,
  fieldLabelClass,
  sizeTableClass,
  sizeTableCommonRowClass,
  sizeTableHeadClass,
  sizeTableRowClass,
  sizeTableSizeCellClass,
  tableHeadCellClass,
} from "../ui/styles";

// One item's lines in a row of the ભાવ page, the main item's or a sub item's: one per size, or one for the item
// itself when it has none. They are the row's lines from start on.
export type PartyRateSegment = {
  item: ItemsProps;
  start: number;
  lines: Pick<PartyRateRowInput, "size_id" | "size_name">[];
};

export type RatePath = `groups.${number}.rows.${number}.${RateField}`;

// The rates of a common row: a size table's fills them into every size, a row's into every rate of the row. They only
// help typing and aren't saved.
export type CommonRates = Record<RateField, string>;

export const emptyCommonRates: CommonRates = {
  cutting_rate: "",
  turning_rate: "",
  vmc_rate: "",
};

type RowCommonRatesProps = {
  // The row's main item.
  item: ItemsProps;
  // The party's process types; only these get rate boxes.
  types: PartyTypeProps[];
  rates: CommonRates;
  onChange: (field: RateField, text: string) => void;
};

// The common row of a main item with sub items, whose rates go to every rate of the row: each sub item's, every size
// included (the main item has none of its own). Laid out like the size tables' common row, so its boxes line up with
// their columns, with labels of its own as it has no header.
export const RowCommonRates = ({
  item,
  types,
  rates,
  onChange,
}: RowCommonRatesProps) => (
  <div className={cx(sizeTableClass, "md:col-span-12")}>
    <div className={commonRateRowClass}>
      <div className="flex min-w-0 items-start gap-2 md:col-span-3">
        <LuLayers className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#34699A]" />
        <div className="min-w-0">
          <div className="font-semibold text-[#113F67]">આખી આઈટમ</div>
          <p className="text-xs text-gray-500">
            અહીં લખેલો ભાવ બધી સબ આઈટમ માં ભરાશે
          </p>
        </div>
      </div>

      {types.map((type) => {
        const field = rateField(type.value);
        const inputId = `party-rate-${item.id}-all-${field}`;

        return (
          <div key={type.value} className="space-y-1 md:col-span-3">
            <label htmlFor={inputId} className={fieldLabelClass}>
              {rateLabel(type)}
            </label>
            <RateInput
              id={inputId}
              value={rates[field]}
              placeholder="દા.ત. 120"
              aria-label={`${item.name} આખી આઈટમ ${rateLabel(type)}`}
              onChange={(text) => onChange(field, text)}
            />
          </div>
        );
      })}
    </div>
  </div>
);

type Props = {
  control: Control<PartyRatesFormInput>;
  getValues: UseFormGetValues<PartyRatesFormInput>;
  setValue: UseFormSetValue<PartyRatesFormInput>;
  // The row's place on the form.
  groupIndex: number;
  segment: PartyRateSegment;
  // The party's process types; only these get rate boxes.
  types: PartyTypeProps[];
  // Set for each sub item of an item built from sub items, so its table says whose it is. The first has no line above.
  isSubItem?: boolean;
  first?: boolean;
  // The rates the item kept from before it had sizes, which saving drops.
  dropped?: string[];
  hasError: boolean;
  // The size table's common row. The row keeps it, so that its own common row can fill it too.
  commonRates: CommonRates;
  onCommonRate: (itemId: number, field: RateField, text: string) => void;
};

// The item's rates as on the item page, for the party's types only: a box per rate while it has no sizes, or the size
// table, with a row of rates per size under a common row whose rates go to every size. Sizes come from the item page,
// in reading order, with the size unit (94 mm).
const PartyItemRates = ({
  control,
  getValues,
  setValue,
  groupIndex,
  segment,
  types,
  isSubItem,
  first,
  dropped,
  hasError,
  commonRates,
  onCommonRate,
}: Props) => {
  const sizeUnit = useAppSelector(getSizeUnit);

  const { item, start, lines } = segment;
  const hasSizes = lines.some((line) => line.size_id !== null);

  // Built when used, so the paths follow the row when a row above it is removed.
  const ratePath = (lineIndex: number, field: RateField) =>
    `groups.${groupIndex}.rows.${start + lineIndex}.${field}` as RatePath;

  // A common rate goes to that process on every size, a blank one too. Each size can still be changed on its own.
  const handleCommonRate = (field: RateField, text: string) => {
    onCommonRate(item.id, field, text);

    lines.forEach((_line, lineIndex) => {
      const name = ratePath(lineIndex, field);
      if (getValues(name) !== text) setValue(name, text, { shouldDirty: true });
    });
  };

  // One line's rate box. The ids are the ones the page had before it had rows.
  const rateBox = (
    lineIndex: number,
    field: RateField,
    inputId: string,
    ariaLabel: string
  ) => (
    <Controller
      control={control}
      name={ratePath(lineIndex, field)}
      render={({ field: rate }) => (
        <RateInput
          ref={rate.ref}
          id={inputId}
          name={rate.name}
          value={rate.value}
          placeholder="દા.ત. 120"
          aria-label={ariaLabel}
          hasError={hasError}
          onChange={rate.onChange}
          onBlur={rate.onBlur}
        />
      )}
    />
  );

  return (
    <div
      className={cx(
        "space-y-3",
        isSubItem && !first && "border-t border-gray-200 pt-4"
      )}
    >
      {isSubItem && (
        <div className="min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="break-words font-semibold text-[#113F67]">
              ↳ {item.name}
            </span>
            <span className="text-xs text-gray-500">
              · સબ આઈટમ, નંગ: {item.pieces ?? "-"}
            </span>
          </div>
          {item.description && (
            <div className="break-words text-xs text-gray-500">
              {item.description}
            </div>
          )}
        </div>
      )}

      {dropped && (
        <p className="flex items-center text-sm text-orange-700">
          <LuAlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
          સેવ કરતાં સાઈઝ વગરના ભાવ ({dropped.join(", ")}) કાઢી નાખવામાં
          આવશે
        </p>
      )}

      {hasSizes ? (
        // The item page's table without the cut length, which is the item's own: each rate as wide as there.
        <div className={sizeTableClass}>
          <div className={sizeTableHeadClass}>
            <div className={cx(tableHeadCellClass, "text-sm md:col-span-3")}>
              સાઈઝ
            </div>
            {types.map((type) => (
              <div
                key={type.value}
                className={cx(tableHeadCellClass, "text-sm md:col-span-3")}
              >
                {rateLabel(type)} (વૈકલ્પિક)
              </div>
            ))}
          </div>

          <div className={sizeTableCommonRowClass}>
            <div className="flex min-w-0 items-start gap-2 md:col-span-3">
              <LuLayers className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#34699A]" />
              <div className="min-w-0">
                <div className="font-semibold text-[#113F67]">બધી સાઈઝ</div>
                <p className="text-xs text-gray-500">
                  અહીં લખેલો ભાવ દરેક સાઈઝ માં ભરાશે
                </p>
              </div>
            </div>

            {types.map((type) => {
              const field = rateField(type.value);
              const inputId = `party-rate-${item.id}-sizes-all-${field}`;

              return (
                <div key={type.value} className="space-y-1 md:col-span-3">
                  <label
                    htmlFor={inputId}
                    className={cx(fieldLabelClass, "md:hidden")}
                  >
                    {rateLabel(type)}
                  </label>
                  <RateInput
                    id={inputId}
                    value={commonRates[field]}
                    placeholder="દા.ત. 120"
                    aria-label={`${item.name} બધી સાઈઝ ${rateLabel(type)}`}
                    onChange={(text) => handleCommonRate(field, text)}
                  />
                </div>
              );
            })}
          </div>

          {lines.map((line, lineIndex) => {
            const label = sizeLabel(line.size_name ?? "", sizeUnit);

            return (
              <div
                key={line.size_id}
                className={cx(
                  sizeTableRowClass,
                  lineIndex > 0 && "border-t border-gray-200"
                )}
              >
                <div className={sizeTableSizeCellClass}>
                  <LuRuler className="h-4 w-4 flex-shrink-0 text-[#34699A]" />
                  <span className="break-words">{label}</span>
                </div>

                {/* On phones each rate sits under the size with its own label. */}
                {types.map((type) => {
                  const field = rateField(type.value);
                  const inputId = `party-rate-${item.id}-size-${line.size_id}-${field}`;

                  return (
                    <div key={type.value} className="space-y-1 md:col-span-3">
                      <label
                        htmlFor={inputId}
                        className={cx(fieldLabelClass, "md:hidden")}
                      >
                        {rateLabel(type)}
                      </label>
                      {rateBox(
                        lineIndex,
                        field,
                        inputId,
                        `${item.name} ${label} ${rateLabel(type)}`
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      ) : (
        // Like the item page's own rate boxes.
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {types.map((type) => {
            const field = rateField(type.value);
            const inputId = `party-rate-${item.id}-${field}`;

            return (
              <div key={type.value} className="space-y-2">
                <label htmlFor={inputId} className={fieldLabelClass}>
                  {rateLabel(type)} (વૈકલ્પિક)
                </label>
                {rateBox(0, field, inputId, `${item.name} ${rateLabel(type)}`)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// The row re-renders on every keystroke in it, and all of these props stay the same.
export default React.memo(PartyItemRates);
